// =============================================================================
// features/admin/feature-flags/statusChange.ts — Lógica pura da transição de
// status de uma flag (F38-S16).
//
// Antes deste slot o toggle era binário: `isEnabled ? 'disabled' : 'enabled'`
// (FlagsTable.tsx). Uma flag `internal_only` tinha `isEnabled === false`, então
// um clique mandava `'enabled'` — promovendo silenciosamente "só o time
// interno" para "todo mundo", sem aviso.
//
// Regra desta função: a ÚNICA transição que exige confirmação explícita é
// `internal_only → enabled`, porque é a única que amplia quem vê a flag (o
// avaliador do backend ignora a audiência inteira em `enabled` — ver
// audienceImpact.ts). Todas as outras transições (incluindo `enabled →
// internal_only`, que RESTRINGE) aplicam direto, como o toggle sempre fez.
// =============================================================================

import type {
  FeatureFlagStatus,
  PatchFeatureFlagBody,
} from '../../../hooks/admin/useFeatureFlags.types';

/** true quando a transição de `from` para `to` amplia a audiência sem aviso. */
export function requiresPromotionConfirmation(
  from: FeatureFlagStatus,
  to: FeatureFlagStatus,
): boolean {
  return from === 'internal_only' && to === 'enabled';
}

export type StatusChangeDecision =
  | { kind: 'noop' }
  | { kind: 'apply'; status: FeatureFlagStatus }
  | { kind: 'confirm'; from: FeatureFlagStatus; to: FeatureFlagStatus };

/**
 * Decide o que fazer quando o operador pede para mudar `currentStatus` para
 * `requestedStatus`. Função pura — a UI (StatusControl/FeatureFlagsPage)
 * só executa a decisão, nunca decide sozinha se precisa confirmar.
 */
export function resolveStatusChange(
  currentStatus: FeatureFlagStatus,
  requestedStatus: FeatureFlagStatus,
): StatusChangeDecision {
  if (currentStatus === requestedStatus) {
    return { kind: 'noop' };
  }
  if (requiresPromotionConfirmation(currentStatus, requestedStatus)) {
    return { kind: 'confirm', from: currentStatus, to: requestedStatus };
  }
  return { kind: 'apply', status: requestedStatus };
}

/**
 * Corpo do PATCH para uma mudança de status. Contém SÓ `status`, nunca
 * `audience` — o backend só substitui a audiência quando o campo está
 * presente no body (repository.ts#updateFlag), então mudar de estado nunca
 * apaga ou reseta a audiência configurada (DoD: "audiência preservada ao
 * trocar de estado").
 */
export function buildStatusPatchBody(status: FeatureFlagStatus): PatchFeatureFlagBody {
  return { status };
}
